import React, { useRef } from 'react';
import './Overlay.scss';

import { CSSTransition } from 'react-transition-group';
import { getPrefixedDataAttributes } from '@/components/utils/DomUtils';
import {
  defCloseIconWrapperStyles,
  defaultCloseIconStyles,
  defaultModalStyles,
  defaultOverlayStyles,
} from '@/components/utils/DefaultStyles';
import Icon from '@/components/Icon/Icon';
import type { IOverlayProps } from '@/components/utils/Interfaces';
import ReactPortal from '@/components/Overlay/ReactPortal';
import { CART_SUMMARY_MODAL_CLOSE_WRAPPER_CLASS } from '@/constants/Order';

/** This is a reusable overlay component that can be used to create modal dialogs or
 *  other UI elements that need to be rendered on top of other content in our application.
 *  It uses the `createPortal` function from `react-dom` to render content outside
 *  of the current component hierarchy and into a different part of the DOM tree */
const Overlay: React.FC<IOverlayProps> = ({ isOpen, disableScrollLock = false, ...props }) => {
  const nodeRef = useRef(null);

  if (!isOpen) {
    return null;
  }

  const {
    overlayClassName,
    children,
    dataAttributes,
    onCloseClick,
    modalStyles,
    overlayStyles,
    closeIconWrapperStyles,
    closeIconPath,
    closeBtnDataAttributes,
    closeIconClassName,
    modalClassName,
    closeIconWrapperClass,
    onBackdropClick,
  } = props;

  const isCartSummaryModalClose = closeIconWrapperClass === CART_SUMMARY_MODAL_CLOSE_WRAPPER_CLASS;

  const renderCloseControl = (): React.ReactNode => {
    if (isCartSummaryModalClose && closeIconPath) {
      return (
        <div className={closeIconWrapperClass}>
          <button
            type="button"
            className={`${closeIconWrapperClass}__btn`}
            aria-label="Close"
            onClick={onCloseClick}
            {...getPrefixedDataAttributes(closeBtnDataAttributes, 'OverlayClose')}
          >
            <Icon
              className={closeIconClassName}
              size="default"
              height="10px"
              width="10px"
              source={closeIconPath}
            />
          </button>
        </div>
      );
    }

    if (closeIconPath) {
      return (
        <button
          type="button"
          className={['overlay-close-button', closeIconWrapperClass].filter(Boolean).join(' ')}
          style={closeIconWrapperStyles}
          onClick={onCloseClick}
          aria-label="Close"
          {...getPrefixedDataAttributes(closeBtnDataAttributes, 'OverlayClose')}
        >
          <Icon
            className={closeIconClassName}
            height={defaultCloseIconStyles.height}
            width={defaultCloseIconStyles.width}
            source={closeIconPath}
          />
        </button>
      );
    }

    return <div className={closeIconWrapperClass} style={closeIconWrapperStyles} />;
  };

  return (
    <ReactPortal wrapperId="react-portal-root" disableScrollLock={disableScrollLock}>
      <CSSTransition
        in={isOpen}
        timeout={300}
        unmountOnExit
        classNames={{
          enter: 'fade-in',
          exit: 'fade-out',
        }}
        nodeRef={nodeRef}
      >
        <div className="cp-modal-container" ref={nodeRef}>
          <button
            type="button"
            className={`cp-modal-backdrop ${overlayClassName ?? ''}`}
            style={overlayStyles}
            {...getPrefixedDataAttributes(dataAttributes, 'Overlay')}
            onClick={onBackdropClick}
          />
          <div
            className={`cp-modal ${modalClassName} ${isOpen ? 'open' : 'closed'}`}
            style={modalStyles}
            tabIndex={-1}
            ref={nodeRef}
          >
            {renderCloseControl()}
            {children}
          </div>
        </div>
      </CSSTransition>
    </ReactPortal>
  );
};

Overlay.defaultProps = {
  modalStyles: defaultModalStyles,
  overlayStyles: defaultOverlayStyles,
  closeIconWrapperStyles: defCloseIconWrapperStyles,
  closeBtnDataAttributes: { id: 'overlay-close-btn' },
  closeIconClassName: 'overlay-close-icon',
};

Overlay.displayName = 'Overlay';

export default Overlay;
